import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { ArrowRight, Phone, Quote } from "lucide-react";
import {
  OrnamentDivider,
  WaveDivider,
  BotanicalCorner,
  Fleuron,
} from "@/components/Ornament";
import {
  Lemon,
  OliveBranch,
  CitrusGarland,
  LemonBranch,
  SunGlow,
  SunWithRays,
  SeaWaves,
} from "@/components/ItalianOrnaments";
import { AmalfiSilhouette } from "@/components/AmalfiSilhouette";
import { MajolicaBand } from "@/components/MajolicaBand";
import { LemonBranchImg, AmalfiCoastImg } from "@/components/WatercolourArt";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Marquee } from "@/components/Marquee";
import { StarRating } from "@/components/StarRating";
import { mixPhotos } from "@/data/mixPhotos";
import { getReviews } from "@/lib/api";

const ATMOSPHERE_IMG = "/illustrations/garden_watercolor.png";

const Home = () => {
  const { t, lang } = useLanguage();
  const [reviewsData, setReviewsData] = useState({ items: [], stats: { average: 0, count: 0 } });

  useEffect(() => {
    let mounted = true;
    getReviews(3)
      .then((data) => {
        if (!mounted) return;
        setReviewsData(data || { items: [], stats: { average: 0, count: 0 } });
      })
      .catch(() => {
        if (!mounted) return;
        setReviewsData({ items: [], stats: { average: 0, count: 0 } });
      });
    return () => {
      mounted = false;
    };
  }, []);

  const dateLocale = lang === "ru" ? "ru-RU" : lang === "en" ? "en-GB" : "lv-LV";
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(dateLocale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return "";
    }
  };

  const highlights = t("home.highlights") || [];
  const marqueeItems = t("marquee") || [];
  const quotes = t("press.quotes") || [];
  const homeReviews = reviewsData.items || [];
  const reviewsStats = reviewsData.stats || { average: 0, count: 0 };

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* ============== HERO – Amalfi premium ============== */}
      <section
        data-testid="hero-section"
        className="relative h-[100svh] min-h-[720px] w-full overflow-hidden paper-grain"
      >
        {/* Amalfi azure sky gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 80% 60% at 78% 20%, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0.18) 40%, rgba(255, 255, 255, 0) 70%),
              linear-gradient(180deg,
                #CFE5F2 0%,
                #BAD8EB 25%,
                #A6CEE3 55%,
                #8FBED7 100%)
            `,
          }}
        />

        {/* Sun haze top-right */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(255,236,180,0.55) 0%, rgba(255,236,180,0.18) 40%, rgba(255,236,180,0) 70%)",
            filter: "blur(20px)",
          }}
        />

        {/* Garden watercolour banner — bottom of hero */}
        <img
          src="/illustrations/garden_watercolor.png"
          alt=""
          aria-hidden="true"
          className="absolute left-0 right-0 bottom-0 w-full pointer-events-none select-none"
        />

        {/* Soft fade overlay to blend the watercolour with hero gradient */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 bottom-0 h-32 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(166,206,227,0) 0%, rgba(143,190,215,0.25) 100%)",
          }}
        />

        {/* Sea horizon shimmer */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: "60%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.65) 35%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.65) 65%, transparent 100%)",
            opacity: 0.7,
          }}
        />

        {/* Soft film grain */}
        <div
          aria-hidden="true"
          className="absolute inset-0 mix-blend-overlay opacity-[0.16] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
            backgroundSize: "200px 200px",
          }}
        />

        {/* Watercolour lemon branches — top corners */}
        <LemonBranchImg className="absolute top-20 -left-6 w-44 md:w-56 lg:w-80 opacity-95 pointer-events-none hidden md:block" />
        <LemonBranchImg
          flip
          className="absolute top-20 -right-6 w-44 md:w-56 lg:w-80 opacity-95 pointer-events-none hidden md:block"
        />

        {/* Content */}
        <div className="relative h-full container-roze flex flex-col">
          <div className="flex-1 flex items-center pt-56 md:pt-64 lg:pt-72 pb-20">
            <div className="max-w-4xl animate-fade-up">
              <h1 className="font-serif font-light text-foreground text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-tight">
                {t("hero.title_1")}
                <span
                  className="block italic font-light mt-1"
                  style={{ color: "#1F5C8A" }}
                >
                  {t("hero.title_2")}
                </span>
              </h1>

              <div className="mt-14 flex flex-wrap items-center gap-4">
                <Link
                  to="/reserve"
                  data-testid="hero-reserve-cta"
                  className="group inline-flex items-center gap-3 px-9 py-4 text-white hover:opacity-90 transition-opacity text-[11px] uppercase tracking-[0.24em]"
                  style={{ backgroundColor: "#1F5C8A" }}
                >
                  {t("hero.cta_reserve")}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== INTRO – editorial split ============== */}
      <section
        data-testid="intro-section"
        className="relative py-24 md:py-32 paper-grain"
        style={{ backgroundColor: "#DCEBF5" }}
      >
        <div
          aria-hidden="true"
          className="watercolor w-[40rem] h-[40rem] -top-24 -right-32"
          style={{
            background:
              "radial-gradient(circle at center, rgba(184,153,104,0.18) 0%, rgba(184,153,104,0) 70%)",
          }}
        />
        <div className="container-roze grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center relative">
          <ScrollReveal className="md:col-span-7 order-2 md:order-1 relative">
            <div className="aspect-[5/4] overflow-hidden frame-vintage">
              <AmalfiCoastImg
                className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-1000"
                alt="Amalfi coast watercolour"
              />
            </div>
            <Lemon className="absolute -top-6 -right-6 w-16 md:w-20 text-[#E0B23E] animate-drift" />
            <OliveBranch className="absolute -bottom-4 -left-8 w-32 md:w-40 text-accent opacity-80 hidden md:block" />
          </ScrollReveal>

          <div className="md:col-span-5 order-1 md:order-2 md:pl-6">
            <ScrollReveal>
              <div className="overline mb-5 flex items-center gap-3">
                <Fleuron className="h-3 w-3 text-gold" />
                {t("home.intro_overline")}
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
                {t("home.intro_title")}
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={1}>
              <p className="drop-cap mt-8 text-base md:text-lg text-foreground/80 leading-[1.85] max-w-xl font-light">
                {t("home.intro_body_1")}
              </p>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <p className="mt-6 text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl font-light">
                {t("home.intro_body_2")}
              </p>

              <div className="mt-8 flex items-center gap-4">
                <Link
                  to="/about"
                  data-testid="home-intro-cta"
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-primary hover:text-primary/80 transition-colors border-b border-primary/40 pb-1"
                >
                  {t("home.intro_cta")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Fleuron className="h-3 w-3 text-gold/70" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============== ORNAMENT DIVIDER ============== */}
      <div className="py-2" style={{ backgroundColor: "#DCEBF5" }}>
        <div className="container-roze">
          <ScrollReveal>
            <OrnamentDivider />
          </ScrollReveal>
        </div>
      </div>

      {/* ============== HIGHLIGHTS BENTO ============== */}
      <section data-testid="highlights-section" className="relative py-20 md:py-24 paper-grain" style={{ backgroundColor: "#C2DDED" }}>
        <div className="container-roze relative">
          <ScrollReveal className="max-w-2xl mb-14">
            <div className="overline mb-5">{t("home.highlights_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight">
              {t("home.highlights_title")}
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[0, 1, 2].map((i) => {
              const photo = mixPhotos[i % mixPhotos.length];
              return (
                <ScrollReveal key={photo.src} delay={i} className="aspect-[4/5] overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover hover:scale-[1.05] transition-transform duration-1000"
                  />
                </ScrollReveal>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 mt-12 md:mt-16">
            {highlights.map((h, idx) => (
              <ScrollReveal
                key={idx}
                delay={idx + 1}
                data-testid={`home-highlight-${idx}`}
                className="border-t border-gold/40 pt-6 relative"
              >
                <div className="absolute -top-3 left-0 pr-3" style={{ backgroundColor: "#C2DDED" }}>
                  <Fleuron className="h-3.5 w-3.5 text-gold" />
                </div>
                <div className="overline text-primary/70 mb-3">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight mb-3">
                  {h.title}
                </h3>
                <p className="text-foreground/75 leading-relaxed font-light">{h.body}</p>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CHEF / PULL QUOTE ============== */}
      <section data-testid="chef-section" className="relative py-16 md:py-24 paper-grain" style={{ backgroundColor: "#C2DDED" }}>
        <div className="container-roze">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <ScrollReveal className="md:col-span-5 md:order-2">
              <div className="overline mb-5">{t("chef.eyebrow")}</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight">
                <span className="italic text-primary">“</span>
                {t("chef.title")}
                <span className="italic text-primary">”</span>
              </h2>
              <p className="mt-8 text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl font-light">
                {t("chef.body")}
              </p>
              <div className="mt-8 flex items-center gap-4">
                <Fleuron className="h-3.5 w-3.5 text-gold" />
                <span className="font-serif italic text-xl text-foreground/70">
                  {t("chef.sign")}
                </span>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={1} className="md:col-span-7 md:order-1">
              <div className="aspect-[5/4] overflow-hidden frame-vintage">
                <img
                  src={ATMOSPHERE_IMG}
                  alt="Roze garden in watercolour"
                  className="w-full h-full object-cover"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============== REVIEWS PREVIEW ============== */}
      {homeReviews.length > 0 && (
        <section
          data-testid="home-reviews-section"
          className="relative py-24 md:py-32 paper-grain"
          style={{ backgroundColor: "#C2DDED" }}
        >
          <div className="container-roze relative">
            <ScrollReveal className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
              <div className="overline mb-5 flex items-center justify-center gap-3">
                <Fleuron className="h-3 w-3 text-gold" />
                {t("reviews.home_overline")}
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight">
                {t("reviews.home_title")}
              </h2>
              {reviewsStats.count > 0 && (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <StarRating
                    value={Math.round(reviewsStats.average)}
                    readOnly
                    size="md"
                    testid="home-reviews-avg-stars"
                  />
                  <span className="font-serif text-xl font-light" data-testid="home-reviews-avg-number">
                    {reviewsStats.average.toFixed(1)}
                  </span>
                  <span className="text-xs uppercase tracking-[0.18em] text-foreground/55">
                    · {reviewsStats.count} {t("reviews.based_on_suffix")}
                  </span>
                </div>
              )}
            </ScrollReveal>

            <div
              data-testid="home-reviews-grid"
              className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
            >
              {homeReviews.slice(0, 3).map((review, idx) => (
                <ScrollReveal
                  key={review.id}
                  delay={idx}
                  className="bg-white/80 border border-gold/40 paper-grain p-6 md:p-8 flex flex-col gap-4"
                >
                  <StarRating value={review.rating} readOnly size="sm" testid={`home-review-rating-${review.id}`} />
                  <p className="text-foreground/85 leading-relaxed font-light italic line-clamp-5">
                    “{review.comment}”
                  </p>
                  <div className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between gap-3">
                    <span className="font-serif text-sm text-foreground/70 truncate">
                      {review.name?.trim() || t("reviews.anon")}
                    </span>
                    <span className="text-xs text-foreground/50 whitespace-nowrap">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            <ScrollReveal className="mt-12 flex flex-wrap justify-center items-center gap-4">
              <Link
                to="/reviews"
                data-testid="home-reviews-all-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 text-white hover:opacity-90 transition-opacity text-xs uppercase tracking-[0.22em]"
                style={{ backgroundColor: "#1F5C8A" }}
              >
                {t("reviews.see_all")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <Link
                to="/reviews"
                data-testid="home-reviews-leave-cta"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#1F5C8A]/55 hover:bg-white/50 transition-colors text-xs uppercase tracking-[0.22em] text-foreground/85"
              >
                {t("reviews.leave_cta")}
              </Link>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ============== CTA BAND ============== */}
      <section
        data-testid="cta-band"
        className="relative py-24 md:py-32 overflow-hidden paper-grain"
        style={{ backgroundColor: "#DCEBF5" }}
      >
        <div className="relative container-roze grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <ScrollReveal className="md:col-span-7">
            <div className="overline mb-5 flex items-center gap-3">
              <Fleuron className="h-3 w-3 text-gold" />
              {t("home.cta_band_overline")}
            </div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight">
              {t("home.cta_band_title")}
            </h2>
            <p className="mt-6 max-w-xl text-base md:text-lg text-foreground/75 leading-relaxed font-light">
              {t("home.cta_band_body")}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={1} className="md:col-span-5 flex flex-col gap-4 md:items-end">
            <Link
              to="/reserve"
              data-testid="home-cta-band-reserve"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 text-white hover:opacity-90 transition-opacity text-xs uppercase tracking-[0.22em]"
              style={{ backgroundColor: "#1F5C8A" }}
            >
              {t("home.cta_band_button")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
            <a
              href="tel:+37126111722"
              data-testid="home-cta-band-call"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#1F5C8A]/55 hover:bg-white/50 transition-colors text-xs uppercase tracking-[0.22em] text-foreground/85"
            >
              <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
              +371 26 111 722
            </a>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
